import {RefObject, useCallback, useEffect, useState, useRef} from 'react';
import * as Sentry from '@sentry/react';
import {useLivenessStore} from '../store/useLivenessStore';
import {useProgressStore} from '../store/useProgressStore';
import {WebcamRefTypes} from 'components/Webcam';
import {FacePosition} from './useFaceDetection';
import {useSyncRef} from '@guestapp/core';
import {throttle} from 'utils/throttle';
import {useLivenessWebSocket, ServerDetectionResponse} from './useLivenessWebSocket';

const FACE_POSITION_THROTTLE = 150;
const FRAME_INTERVAL = 150;

interface UseServerFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  shouldInitialize?: boolean;
}

export const useServerFaceDetection = ({
  videoRef,
  shouldInitialize = true,
}: UseServerFaceDetectionProps) => {
  const [facePosition, setFacePosition] = useState<FacePosition>('unknown');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const currentChallenge = useLivenessStore(state => state.currentChallenge);
  const currentChallengeRef = useSyncRef(currentChallenge);
  const isBlinked = useLivenessStore(state => state.isBlinked);
  const setIsBlinked = useLivenessStore(state => state.setIsBlinked);
  const setProgress = useProgressStore(state => state.setProgress);

  const throttledSetFacePosition = useRef(
    throttle((position: FacePosition) => {
      setFacePosition(position);
    }, FACE_POSITION_THROTTLE),
  ).current;

  const mapServerDirection = useCallback((direction: string): FacePosition => {
    switch (direction.toUpperCase()) {
      case 'LEFT':
        return 'left';
      case 'RIGHT':
        return 'right';
      case 'UP':
        return 'up';
      case 'DOWN':
        return 'down';
      case 'CENTER':
        return 'center';
      default:
        return 'unknown';
    }
  }, []);

  const handleWebSocketMessage = useCallback(
    (response: ServerDetectionResponse) => {
      if (response.type === 'detection') {
        const {is_face_detected, direction, is_blinking, move_percent} = response.data;

        if (is_face_detected && direction === 'CENTER') {
          const mappedPosition = mapServerDirection(direction);
          throttledSetFacePosition(mappedPosition);
        } else if (is_face_detected && move_percent === 100) {
          const mappedPosition = mapServerDirection(direction);
          throttledSetFacePosition(mappedPosition);
        } else {
          throttledSetFacePosition('unknown');
        }

        if (is_blinking && !isBlinked) {
          setIsBlinked(true);
        }

        if (
          move_percent !== null &&
          move_percent !== undefined &&
          currentChallengeRef.current
        ) {
          const shouldUpdateProgress =
            (currentChallengeRef.current === 'TURN_LEFT' &&
              direction.toUpperCase() === 'LEFT') ||
            (currentChallengeRef.current === 'TURN_RIGHT' &&
              direction.toUpperCase() === 'RIGHT') ||
            (currentChallengeRef.current === 'TURN_UP' &&
              direction.toUpperCase() === 'UP') ||
            direction.toUpperCase() === 'CENTER';

          if (shouldUpdateProgress) {
            setProgress(move_percent);
          }
        }
      }
    },
    [
      currentChallengeRef,
      isBlinked,
      mapServerDirection,
      setIsBlinked,
      setProgress,
      throttledSetFacePosition,
    ],
  );

  const {isConnected, sendData} = useLivenessWebSocket({
    onMessage: handleWebSocketMessage,
    autoConnect: shouldInitialize,
  });
  const isConnectedRef = useSyncRef(isConnected);
  const shouldInitializeRef = useSyncRef(shouldInitialize);

  useEffect(() => {
    if (!shouldInitialize) {
      return;
    }

    canvasRef.current = document.createElement('canvas');
    return () => {
      canvasRef.current = null;
    };
  }, [shouldInitialize]);

  const sendFrame = useCallback(() => {
    if (
      !shouldInitializeRef.current ||
      !isConnectedRef.current ||
      !canvasRef.current ||
      !videoRef?.current?.video
    ) {
      return;
    }

    const video = videoRef.current.video;
    if (video.readyState !== 4) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      async blob => {
        if (!blob) return;

        try {
          const arrayBuffer = await blob.arrayBuffer();
          sendData(arrayBuffer);
        } catch (err) {
          Sentry.captureException(err);
          console.error('Error preparing frame for WebSocket:', err);
        }
      },
      'image/jpeg',
      0.8,
    );
  }, [isConnectedRef, sendData, videoRef, shouldInitializeRef]);

  const startDetection = useCallback(() => {
    if (!shouldInitialize) {
      return;
    }

    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
    }

    frameIntervalRef.current = setInterval(sendFrame, FRAME_INTERVAL);
  }, [sendFrame, shouldInitialize]);

  useEffect(() => {
    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
    };
  }, []);

  const isCorrectPosition = useCallback(() => {
    if (!currentChallenge || !facePosition) return false;

    switch (currentChallenge) {
      case 'TURN_LEFT':
        return facePosition === 'left';
      case 'TURN_RIGHT':
        return facePosition === 'right';
      case 'TURN_UP':
        return facePosition === 'up';
      case 'BLINK':
        return isBlinked;
      default:
        return false;
    }
  }, [currentChallenge, facePosition, isBlinked]);

  return {
    isModelLoading: shouldInitialize ? !isConnected : false,
    facePosition,
    startDetection,
    isCorrectPosition,
  };
};
